// Posts Page
// Author : Aysegul Yalcinkaya

<template>
  <b-colxx class="disable-text-selection">
    <!-- Top Menu -->
    <TopMenu
      :title="$t('menu.your-posts')"
      :config="{}"
    />

    <!-- View Toggle + Filter Buttons -->
    <b-row class="mb-4">
      <b-colxx xxs="12">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <b-button-group>
              <b-button :class="{ 'active': viewMode === 'list' }" variant="outline-primary" @click="viewMode='list'">List</b-button>
              <b-button :class="{ 'active': viewMode === 'calendar' }" variant="outline-primary" @click="viewMode='calendar'">Calendar</b-button>
            </b-button-group>
          </div>
          <div class="d-flex align-items-center">
            <b-button
              variant="outline-success"
              size="sm"
              @click="refreshPosts"
              :disabled="refreshing"
              class="mr-2"
            >
              <i class="simple-icon-refresh" v-if="!refreshing"></i>
              <b-spinner small v-else></b-spinner>
              {{ refreshing ? 'Refreshing...' : 'Refresh' }}
            </b-button>
            <b-button-group>
              <b-button
                variant="outline-primary"
                :class="{ 'active': currentFilter === 'all' }"
                @click="setFilter('all')"
              >
                All
              </b-button>
              <b-button
                variant="outline-primary"
                :class="{ 'active': currentFilter === 'pending' }"
                @click="setFilter('pending')"
              >
                Pending
              </b-button>
              <b-button
                variant="outline-primary"
                :class="{ 'active': currentFilter === 'posted' }"
                @click="setFilter('posted')"
              >
                Posted
              </b-button>
              <b-button
                variant="outline-primary"
                :class="{ 'active': currentFilter === 'engaged' }"
                @click="setFilter('engaged')"
              >
                Engaged
              </b-button>
            </b-button-group>
          </div>
        </div>
      </b-colxx>
    </b-row>

    <!-- Content -->
    <template v-if="isLoad">
      <template v-if="viewMode==='list'">
        <div class="mb-3 d-flex justify-content-end">
          <b-button
            variant="danger"
            size="sm"
            @click="onNuclearDelete"
            :disabled="nuking"
          >
            {{ nuking ? 'Deleting Scheduled...' : 'Delete ALL Scheduled (⚠️)' }}
          </b-button>
        </div>
        <div v-if="postsToRender.length > 0">
          <b-row key="thumb">
            <ListPost
              v-for="(item, index) in postsToRender"
              :key="index"
              :id="item.id"
              :cyPrefix="`list-item-post-${index}`"
              :data="item"
              :highlight="index === 0"
            />
          </b-row>
        </div>
        <div v-else class="text-center mt-4">
          <h5>No posts found.</h5>
          <p v-if="currentFilter === 'engaged'">
            None of your posts have any engagement.
          </p>
          <p v-else>
            You haven't posted anything in the past 30 days.
          </p>
        </div>
      </template>
      <template v-else>
        <div class="mb-3 d-flex align-items-center justify-content-between">
          <b-form-radio-group
            v-model="calendarMode"
            :options="[
              { text: 'Month', value: 'month' },
              { text: 'Week', value: 'week' }
            ]"
            buttons
            button-variant="outline-primary"
            size="sm"
          />
          <b-button
            variant="danger"
            size="sm"
            @click="onNuclearDelete"
            :disabled="nuking"
          >
            {{ nuking ? 'Deleting Scheduled...' : 'Delete ALL Scheduled (⚠️)' }}
          </b-button>
        </div>
        <PostsWeekCalendar
          v-if="calendarMode==='week'"
          :key="calendarKey"
          v-model="calendarSelectedDate"
          :posts="filteredCalendarPosts"
          @post-click="openPostModal"
        />
        <PostsCalendar
          v-else
          :key="calendarKey"
          v-model="calendarSelectedDate"
          :posts="filteredCalendarPosts"
          @change="onCalendarDateChange"
          @day-click="openDayModal"
          @post-click="openPostModal"
        />
      </template>
    </template>

    <template v-else>
      <div data-cy="div-loading" class="loading"></div>
    </template>

    <!-- Day Modal: shows all posts on selected date -->
    <b-modal
      v-model="showDayModal"
      size="xl"
      hide-footer
      :title="dayModalData.date ? `Posts on ${dayModalData.date.toLocaleDateString()}` : 'Posts'"
    >
      <b-row>
        <ListPost
          v-for="(item, index) in dayModalData.posts"
          :key="'day-'+index"
          :id="item.id"
          :cyPrefix="`modal-day-post-${index}`"
          :data="item"
          :highlight="false"
        />
      </b-row>
    </b-modal>

    <!-- Post Modal: single post with analytics and link to platform -->
    <b-modal
      v-model="showPostModal"
      size="lg"
      hide-footer
      title="Post Details"
    >
      <div v-if="postModalData">
        <b-row>
          <ListPost
            :id="postModalData.id"
            cyPrefix="modal-post"
            :data="postModalData"
            :highlight="false"
          />
        </b-row>
        <div class="mt-3" v-if="primaryPostLink(postModalData)">
          <b-button :href="primaryPostLink(postModalData)" target="_blank" variant="primary">
            View on Platform
          </b-button>
        </div>
      </div>
    </b-modal>
  </b-colxx>
</template>


<script>

import DialogAlert from '@/components/Common/DialogAlert'
import TopMenu from '@/components/Forms/TopMenu'
import DialogProduct from '@/components/Products/DialogProduct'
import ListComponent from '@/components/Common/ListComponent'
import ListPost from '@/components/Listing/ListPost'
import PostsCalendar from './PostsCalendar.vue'
import PostsWeekCalendar from './PostsWeekCalendar.vue'

import {EMPTY_PRODUCT} from "@/models/products/product"

import {TYPE_PROMOTION, getDealType} from '@/models/promotion'

import {PATH_MAP_PROMOTIONS} from "@/constants/common.js";

// Notifications
import {
  addNotifySuccess,
  addNotifyWarning,
  addNotifyFail,
} from "@/utils/notify.js"
import {isNullOrUndefined, isResponseOK, formatNumber} from "@/utils/general.js"

import {mapGetters, mapActions, mapMutations} from 'vuex'
import {getPostHistory} from "@/api/social_media_post";



export default {
  components: {
    ListPost,
    TopMenu,
    PostsCalendar,
    PostsWeekCalendar,
  },

  data() {
    return {
      isLoad: false,      // Is page loaded.
      search: "",          // Search keyword
      currentFilter: 'all', // Current filter: 'all', 'pending', 'posted', 'engaged'
      viewMode: 'list',
      calendarMode: 'month',
      calendarSelectedDate: new Date(),
      showDayModal: false,
      dayModalData: { date: null, posts: [] },
      showPostModal: false,
      postModalData: null,
      calendarKey: 0,
      nuking: false,
      refreshing: false,
    }
  },

  methods: {
    // Notifications.
    addNotifySuccess,
    addNotifyWarning,
    addNotifyFail,

    // Map actions.
    ...mapActions("social_media_post", ["loadPostHistory","setAllPosts"]),
    ...mapActions("social_media_accounts", ["getAyrshareUserInfo"]),

    init() {
      if (!isNullOrUndefined(this.ayrshareKey)) {
        this.getAyrshareUserInfo({
          profileKey: this.ayrshareKey,
          onSuccess: this.onGetAyrshareUserInfoSuccess,
          onFail: this.onGetAyrshareUserInfoFail
        })
      }
    },
    onGetAyrshareUserInfoSuccess(response) {
      if (isResponseOK(response)) {
        this.accounts = response.data.displayNames;
      }
    },
    onGetAyrshareUserInfoFail(response) {
      console.log("on check fail" + response);
    },

    /**
     * Get all the data for the page.
     */
    getAll() {
      // Get posts and store locally
      this.getPosts();
    },

    async getPosts() {
      console.log("🔑 ProfileKey used for history request:", this.ayrshareKey);
      try {
        console.log("⏳ Awaiting loadPostHistory...");

        const response = await this.loadPostHistory({
          values: {
            profileKey: this.ayrshareKey,
            platform: 'instagram',  // Fetch Instagram posts including manually created ones (requires Premium+ plan)
          },
          fetchAnalytics: true
        });
        console.log("✅ Got response from loadPostHistory:", response);

        this.isLoad = true;

        if (response && response.status === 200) {
          // Filter out deleted/invalid posts
          let postsData = response.data;

          // Log all posts to see their statuses
          console.log("📊 ALL POSTS BEFORE FILTERING:", JSON.stringify(postsData.history?.map(p => ({
            id: p.id,
            status: p.status,
            refId: p.refId,
            created: p.created,
            post: p.post?.substring(0, 50)
          })), null, 2));

          if (postsData && postsData.history && Array.isArray(postsData.history)) {
            const originalCount = postsData.history.length;

            // Filter out posts with error status, deleted status, or any invalid states
            postsData.history = postsData.history.filter(post => {
              // List of invalid statuses to filter out
              const invalidStatuses = ['deleted', 'error', 'failed', 'cancelled', 'canceled'];
              const isValidStatus = !invalidStatuses.includes(post.status?.toLowerCase());
              const hasValidId = post.id || (Array.isArray(post.postIds) && post.postIds.length > 0);

              // Additional check: if post was created more than 90 days ago and is still pending, it's likely stale
              const isStale = post.status === 'pending' && post.created &&
                ((Date.now() - new Date(post.created).getTime()) > (90 * 24 * 60 * 60 * 1000));

              // Filter out posts that are likely deleted (analytics 404 + >24h old)
              const isPossiblyDeleted = post._possiblyDeleted === true;

              const shouldKeep = isValidStatus && hasValidId && !isStale && !isPossiblyDeleted;

              if (!shouldKeep) {
                console.log("🗑️ FILTERING OUT POST:", {
                  id: post.id,
                  refId: post.refId,
                  status: post.status,
                  _possiblyDeleted: post._possiblyDeleted,
                  reason: !isValidStatus ? 'invalid status' :
                          !hasValidId ? 'no valid ID' :
                          isStale ? 'stale pending' :
                          isPossiblyDeleted ? 'possibly deleted (404 analytics + >24h old)' : 'unknown',
                  created: post.created,
                  post: post.post?.substring(0, 50)
                });
              }

              return shouldKeep;
            });

            console.log(`✅ FILTERED POSTS: ${originalCount} → ${postsData.history.length} (removed ${originalCount - postsData.history.length})`);
          }

          this.setAllPosts(postsData);
          console.log("📄 Posts loaded into store:", this.$store.getters["social_media_post/getAllPosts"]);
        } else {
          console.warn("❌ Response not OK, setting empty post list");
          this.setAllPosts([]);
        }
      } catch (err) {
        console.warn("⚠️ No posts or failed request:", err);
        this.isLoad = true;
        this.setAllPosts([]);
      }
    },

    async refreshPosts() {
      console.log("🔄 Manual refresh triggered");
      this.refreshing = true;
      try {
        await this.getPosts();
        this.$notify('success', 'Refreshed', 'Posts have been refreshed', { duration: 2000 });
      } catch (err) {
        this.$notify('error', 'Error', 'Failed to refresh posts', { duration: 3000 });
      } finally {
        this.refreshing = false;
      }
    },

    handleVisibilityChange() {
      if (!document.hidden) {
        // Page is now visible, refresh posts
        console.log("👁️ Page became visible, refreshing posts...");
        this.getPosts();
      }
    },

    setFilter(filter) {
      this.currentFilter = filter;
      this.calendarKey++
    },
    onCalendarDateChange(d) {
      // no-op; computed postsOnSelectedDate reacts to calendarSelectedDate
    },
    openDayModal(payload) {
      this.dayModalData = payload || { date: null, posts: [] }
      this.showDayModal = true
    },
    openPostModal(post) {
      this.postModalData = post
      this.showPostModal = true
    },
    primaryPostLink(post) {
      try {
        if (Array.isArray(post.postIds)) {
          const withUrl = post.postIds.find(p => p.postUrl)
          return withUrl ? withUrl.postUrl : null
        }
        return null
      } catch (e) { return null }
    },
    async onNuclearDelete() {
      try {
        const pending = (this.allPostsArray || []).filter(p => p.status === 'pending');
        if (!pending.length) {
          this.$notify('warning', 'No Scheduled Posts', 'There are no scheduled/pending posts to delete', { duration: 2500 });
          return;
        }
        this.nuking = true;
        const api = await import('@/api/social_media_post');
        const profileKey = this.ayrshareKey;
        for (const p of pending) {
          const id = (Array.isArray(p.postIds) && p.postIds[0] && p.postIds[0].id) ? p.postIds[0].id : (p.id || undefined);
          const refId = p.refId;
          try { await api.deletePost({ profileKey, id, refId }); } catch(e) { /* continue */ }
        }
        this.$notify('success', 'Deleted', 'All scheduled posts deleted (where possible)', { duration: 3000 });
        await this.getPosts();
      } catch(e) {
        this.$notify('error', 'Error', 'Failed to delete scheduled posts', { duration: 3000 });
      } finally {
        this.nuking = false;
      }
    }
  },
  computed: {
    // Map gatters
    ...mapGetters("merchant_profile", ["ayrshareKey"]),
    ...mapGetters("social_media_post", ["getAllPosts"]),
    allPostsArray() {
      // flatten to array for calendar
      const obj = this.getAllPosts || {}
      return Array.isArray(obj.history) ? obj.history : []
    },
    filteredCalendarPosts() {
      const posts = this.allPostsArray
      switch (this.currentFilter) {
        case 'pending':
          return posts.filter(p => p.status === 'pending' || p.status === 'scheduled')
        case 'posted':
          return posts.filter(p => p.status === 'success')
        case 'engaged':
          return posts.filter(p =>
            p.analytics && (
              p.analytics.totalLikes > 0 ||
              p.analytics.totalComments > 0 ||
              p.analytics.totalShares > 0 ||
              p.analytics.totalViews > 0 ||
              p.analytics.totalReach > 0
            )
          )
        default:
          return posts
      }
    },
    postsToRender() {
      if (!this.getAllPosts || !this.getAllPosts.history) return [];
      let posts = this.getAllPosts.history;
      
      // Filter posts based on current filter
      switch (this.currentFilter) {
        case 'pending':
          return posts.filter(post => post.status === 'pending');
        case 'posted':
          return posts.filter(post => post.status === 'success');
        case 'engaged':
          return posts.filter(post => 
            post.analytics && 
            (post.analytics.totalLikes > 0 || 
             post.analytics.totalComments > 0 || 
             post.analytics.totalShares > 0 || 
             post.analytics.totalViews > 0 || 
             post.analytics.totalReach > 0)
          );
        default:
          return posts; // 'all' - return all posts
      }
    },
    postsOnSelectedDate() {
      const d = this.calendarSelectedDate
      const y = d.getFullYear(), m = d.getMonth(), day = d.getDate()
      return this.allPostsArray.filter(p => {
        const s = p.scheduleDate || p.created
        if (!s) return false
        const dt = new Date(s)
        return dt.getFullYear() === y && dt.getMonth() === m && dt.getDate() === day
      })
    },
    debugPosts() {
      console.log("👀 getAllPosts from Vuex in component:", this.getAllPosts);
      return this.getAllPosts;
    },
    formatNumber(value) {
      return formatNumber(value, true);
    }
  },
  mounted() {
    this.$store.dispatch("auth/initializeMerchant");
    this.init();
    this.$nextTick(() => {
      this.getAll();
    });

    // Auto-refresh when page becomes visible
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },

  beforeDestroy() {
    // Clean up event listener
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  },

  activated() {
    // Refresh posts when navigating back to this page (if using keep-alive)
    console.log("📍 Posts page activated, refreshing data...");
    this.getPosts();
  }
};
</script>

<style lang="scss" scoped>
.active {
  background-color: #ff6b35 !important;
  border-color: #ff6b35 !important;
  color: white !important;
}

.btn-group .btn:not(.active) {
  background-color: transparent;
  border-color: #ff6b35;
  color: #ff6b35;
}

.btn-group .btn:not(.active):hover {
  background-color: #ff6b35;
  color: white;
}
</style>

